import React, { useCallback, useEffect, useState } from 'react';

import { useParams } from 'react-router';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { ArticleComment } from '../../../../types/items';
import ArticleCommentEditor from '../../../../components/article-comment-editor/ArticleCommentEditor';
import {
  clearArticleComment,
  createArticleCommentRequest,
  setArticleCommentMessage,
} from '../../../../store/modules/articleComments/actions';
import useStyles from '../styles';
import { selectArticleComment } from '../../../../store/modules/articleComments/selectors';
import { isParseValid } from '../../../../utility/string';
import { Button } from '../../../../components';
import hotKeyConverter from '../../../../utility/keyCodeConverter';
import { filterSideBlocks } from '../../../../services/editorJS/utils';
import PreventChangeRouteModal from '../../../../components/modal-prevent/PreventChangeRouteModal';
import { setAllowChangeProjectStatus } from '../../../../store/modules/projects/actions';

const ArticleCommentForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { id: projectId } = useParams<{id: string}>();
  const { data: message } = useSelector(selectArticleComment);

  const setVal = useCallback((string: string) => {
    dispatch(setArticleCommentMessage(string));
  }, [dispatch]);

  const [disabled, setDisabled] = useState(true);

  const createComment = useCallback(() => {
    if (!disabled) {
      const comment: ArticleComment = {
        message,
        project_id: projectId,
        is_general_comment: true,
      };
      dispatch(createArticleCommentRequest(comment));
    }
  }, [message, dispatch, disabled, projectId]);

  const editorBottomPanel = () => (
    <Grid container direction="row-reverse">
      <Grid item>
        <Button
          disabled={disabled}
          type="button"
          title="Comment"
          variant="contained"
          onClick={createComment}
          classes={{
            root: classes.buttonRoot,
            label: classes.buttonLabel,
          }}
          data-intercom-target="Add button"
        >
          Comment
        </Button>
      </Grid>
    </Grid>
  );

  useEffect(() => () => {
    dispatch(clearArticleComment());
  }, [dispatch]);

  useEffect(() => {
    if (message && isParseValid(message)) {
      let { blocks } = JSON.parse(message);
      blocks = filterSideBlocks(blocks);
      setDisabled(!(blocks && blocks.length));
    } else {
      setDisabled(true);
    }
  }, [message, setDisabled]);

  useEffect(() => {
    const hotKeyHandler = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.keyCode === hotKeyConverter('enter')) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', hotKeyHandler);
    return () => {
      window.removeEventListener('keydown', hotKeyHandler);
    };
  }, [createComment]);

  useEffect(() => {
    dispatch(setAllowChangeProjectStatus(disabled));
    return () => {
      dispatch(setAllowChangeProjectStatus(true));
    };
  }, [dispatch, disabled]);

  return (
    <Grid className={classes.commentForm}>
      <Grid className={classes.commentField}>
        <ArticleCommentEditor
          name="message"
          id={1}
          value={message}
          onChangeValue={setVal}
          bottomPanel={editorBottomPanel()}
          submit={createComment}
          disabled={disabled}
        />
      </Grid>
      <PreventChangeRouteModal preventCancelChanges={!disabled} />
    </Grid>
  );
};

export default ArticleCommentForm;
